// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Alert, Button, Spinner } from '@loa/ui-components';
import { intelligenceApi, type Proposal, type ProposalResponse } from '../../api/intelligenceApi';

/**
 * Where the detail page for a created entity lives — used to make the
 * "Confirmed" card's link clickable. For most tools the `id` arg is
 * the newly created entity (executedActionId). For notes the id is
 * the new note's UUID which doesn't have its own page; we instead
 * route to the PARENT entity's notes section, which is carried on
 * the proposal payload (`entityType` + `entityId`).
 */
function detailUrl(
  toolName: string,
  id: string,
  payload?: Record<string, unknown>,
): string | null {
  switch (toolName) {
    case 'propose_create_relationship':
      return `/relationships/${id}`;
    case 'propose_create_client':
      return `/clients/${id}`;
    case 'propose_add_policy':
      return `/policies/${id}`;
    case 'propose_add_note':
      return noteDetailUrl(payload);
    default:
      return null;
  }
}

/**
 * Build a deep link to the parent entity's notes tab (where the
 * advisor will actually see the note that was just created). Returns
 * null if the proposal payload doesn't carry a recognisable parent.
 *
 * Today only RELATIONSHIP detail has a Notes tab in the advisor-ui;
 * CLIENT / POLICY routes land on the detail page without a notes
 * anchor — still useful as a starting point and ready for when those
 * tabs ship.
 */
function noteDetailUrl(payload?: Record<string, unknown>): string | null {
  if (!payload) return null;
  const entityId = typeof payload.entityId === 'string' ? payload.entityId.trim() : '';
  const entityType = typeof payload.entityType === 'string' ? payload.entityType.toUpperCase() : '';
  if (!entityId) return null;
  switch (entityType) {
    case 'RELATIONSHIP':
      return `/relationships/${entityId}?tab=notes`;
    case 'CLIENT':
      return `/clients/${entityId}?tab=notes`;
    case 'POLICY':
      return `/policies/${entityId}?tab=notes`;
    default:
      return null;
  }
}

/**
 * Short human-readable name for the entity the card just created.
 * Falls back to null if the payload doesn't have the expected fields,
 * in which case the card just shows the entity id (and a link if any).
 */
function entityName(toolName: string, payload?: Record<string, unknown>): string | null {
  if (!payload) return null;
  switch (toolName) {
    case 'propose_create_relationship': {
      const n = payload.profileName;
      return typeof n === 'string' && n.trim() ? n.trim() : null;
    }
    case 'propose_create_client': {
      const f = typeof payload.firstName === 'string' ? payload.firstName : '';
      const l = typeof payload.lastName === 'string' ? payload.lastName : '';
      const full = `${f} ${l}`.trim();
      return full || null;
    }
    case 'propose_add_policy': {
      const n = payload.policyNumber;
      return typeof n === 'string' && n.trim() ? `Policy ${n.trim()}` : null;
    }
    case 'propose_add_note': {
      const t = payload.title;
      return typeof t === 'string' && t.trim() ? t.trim() : null;
    }
    default:
      return null;
  }
}

/** Verb for the "Open …" link, per entity type. */
function openLabel(toolName: string): string {
  switch (toolName) {
    case 'propose_create_relationship':
      return 'Open relationship';
    case 'propose_create_client':
      return 'Open client';
    case 'propose_add_policy':
      return 'Open policy';
    case 'propose_add_note':
      return 'Open notes';
    default:
      return 'Open';
  }
}

export interface ConfirmationCardProps {
  /** Session this proposal belongs to — supplied by the chatbot's host. */
  sessionId: string;
  /** Proposal id parsed out of the `chat-confirmation` fence. */
  proposalId: string;
  /** Notified after a successful confirm or cancel so the host can
   *  append a follow-up system bubble. */
  onResolved?: (outcome: { proposalId: string; status: Proposal['status']; executedActionId?: string }) => void;
}

/**
 * Inline card that renders when Fin emits a {@code chat-confirmation}
 * fence after a {@code propose_*} tool call. The advisor explicitly
 * clicks Confirm or Cancel — Fin cannot self-fire writes.
 *
 * The card fetches the proposal on mount (so it always shows the
 * server-side preview, not a copy the LLM may have mis-quoted),
 * disables the buttons while the request is in flight, and surfaces
 * any 404 / 410 / 4xx errors inline so the advisor knows the proposal
 * has expired or was already actioned.
 */
export function ConfirmationCard({ sessionId, proposalId, onResolved }: ConfirmationCardProps) {
  const [proposal, setProposal] = useState<ProposalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ status: Proposal['status']; executedActionId?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    intelligenceApi
      .getProposal(sessionId, proposalId)
      .then((p) => {
        if (cancelled) return;
        setProposal(p);
        if (p.status !== 'PENDING') setDone({ status: p.status });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load proposal');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, proposalId]);

  const handleConfirm = useCallback(async () => {
    if (!proposal || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await intelligenceApi.confirmProposal(sessionId, proposalId);
      setDone({ status: result.status, executedActionId: result.executedActionId });
      onResolved?.({ proposalId, status: result.status, executedActionId: result.executedActionId });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Confirm failed');
    } finally {
      setSubmitting(false);
    }
  }, [proposal, submitting, sessionId, proposalId, onResolved]);

  const handleCancel = useCallback(async () => {
    if (!proposal || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await intelligenceApi.cancelProposal(sessionId, proposalId);
      setDone({ status: result.status });
      onResolved?.({ proposalId, status: result.status });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cancel failed');
    } finally {
      setSubmitting(false);
    }
  }, [proposal, submitting, sessionId, proposalId, onResolved]);

  const cardStyle: React.CSSProperties = {
    border: '1px solid #fbbf24',
    background: '#fffbeb',
    borderRadius: 8,
    padding: 12,
    margin: '8px 0',
  };

  if (loading) {
    return (
      <div style={cardStyle} data-testid='confirmation-card-loading'>
        <Spinner size='sm' /> Loading proposal…
      </div>
    );
  }
  if (error && !proposal) {
    return (
      <div style={cardStyle} data-testid='confirmation-card-error'>
        <Alert variant='error' header='Couldn’t load proposal'>{error}</Alert>
      </div>
    );
  }
  if (!proposal) return null;

  if (done) {
    const label = done.status === 'CONFIRMED'
      ? '✓ Confirmed'
      : done.status === 'CANCELLED'
        ? '✗ Cancelled'
        : `(${done.status})`;
    const name = entityName(proposal.toolName, proposal.payload);
    const href = done.executedActionId
      ? detailUrl(proposal.toolName, done.executedActionId, proposal.payload)
      : null;
    return (
      <div style={{ ...cardStyle, borderColor: '#a7f3d0', background: '#ecfdf5' }} data-testid='confirmation-card-done'>
        <strong>{label}</strong>
        {name && (
          <div
            style={{ fontSize: 13, marginTop: 4, color: '#064e3b', fontWeight: 500 }}
            data-testid='confirmation-card-entity-name'
          >
            {name}
          </div>
        )}
        {href && done.executedActionId ? (
          <div style={{ fontSize: 12, marginTop: 4 }}>
            <Link
              to={href}
              style={{ color: '#065f46', textDecoration: 'underline' }}
              data-testid='confirmation-card-entity-link'
            >
              {openLabel(proposal.toolName)} →
            </Link>
          </div>
        ) : done.executedActionId ? (
          <div style={{ fontSize: 12, marginTop: 4, color: '#065f46' }}>
            New entity id: <code>{done.executedActionId}</code>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div style={cardStyle} data-testid='confirmation-card'>
      <div style={{ fontWeight: 600, marginBottom: 6, color: '#92400e' }}>
        Fin proposed this — review every field
      </div>
      {proposal.previewMarkdown && (
        <div
          className='confirmation-card-preview'
          style={{ fontSize: 13, marginBottom: 10 }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // The preview block sits inside an already-styled card; we
              // tighten paragraph + list margins so the markdown reads
              // as a compact summary rather than a full document.
              p: ({ children }) => <p style={{ margin: '0 0 6px' }}>{children}</p>,
              ul: ({ children }) => (
                <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>
              ),
              li: ({ children }) => <li style={{ margin: '1px 0' }}>{children}</li>,
              // External links would be unexpected in a write proposal —
              // disable navigation; if Fin ever emits one, it renders as
              // the label only.
              a: ({ children }) => <span>{children}</span>,
            }}
          >
            {proposal.previewMarkdown}
          </ReactMarkdown>
        </div>
      )}
      {error && <Alert variant='error'>{error}</Alert>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button
          variant='filled'
          onClick={handleConfirm}
          disabled={submitting}
          data-testid='confirmation-card-confirm'
        >
          {submitting ? 'Working…' : 'Confirm'}
        </Button>
        <Button
          variant='outlined'
          onClick={handleCancel}
          disabled={submitting}
          data-testid='confirmation-card-cancel'
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default ConfirmationCard;
